import { Box, HStack, Stack, Text, Icon } from '@chakra-ui/react';
import { NestedMunicipality } from '@habity/types';
import { ListSkeleton } from '../../../homes/list/components/ListSkeleton';
import { FaStar } from 'react-icons/fa';

interface MunicipalityCardDataProps {
  municipality: NestedMunicipality;
  isLoading?: boolean;
  variant?: 'default' | 'compact';
}

export const MunicipalityCardData = ({
  municipality,
  isLoading = false,
  variant = 'default',
}: MunicipalityCardDataProps) => {
  const { name, for_sale_homes_count, sold_homes_count, areas_count, rating, rating_count } = municipality || {};

  return (
    <Stack
      spacing="1"
      pt={0}
      pb={1}
      cursor="pointer"
      justifyContent="flex-start"
    >
      {/* Municipality Name */}
      <ListSkeleton isLoaded={!isLoading}>
        <Box mt="1" noOfLines={1}>
          <HStack spacing="1" justifyContent="flex-start">
            <Text
              size="md"
              fontWeight="semibold"
              flexShrink="0"
              color="blackAlpha.900"
              lineHeight={1.5}
              noOfLines={1}
              _groupHover={{ textDecoration: "underline" }}
            >
              {name}
            </Text>
          </HStack>
        </Box>
      </ListSkeleton>

      {/* Rating */}
      {rating && rating_count && (
        <HStack spacing="1" justifyContent="flex-start">
          <ListSkeleton isLoaded={!isLoading}>
            <HStack spacing="1" alignItems="center">
              <Icon as={FaStar} color="yellow.500" boxSize="3" />
              <Text
                color="blackAlpha.800"
                fontSize="sm"
                fontWeight="medium"
                lineHeight={1.2}
              >
                {Number(rating).toFixed(1)}
              </Text>
              <Text
                color="blackAlpha.600"
                fontSize="sm"
                lineHeight={1.2}
              >
                ({rating_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')})
              </Text>
            </HStack>
          </ListSkeleton>
        </HStack>
      )}

      {/* For sale and Sold homes count on same row */}
      <HStack alignItems="baseline" justifyContent="flex-start" spacing="1">
        <ListSkeleton isLoaded={!isLoading}>
          <HStack spacing="1" alignItems="center">
            <Text
              color="blackAlpha.700"
              fontSize={variant === 'compact' ? 'xs' : 'sm'}
              noOfLines={1}
              fontWeight="normal"
            >
              {for_sale_homes_count || 0} {for_sale_homes_count === 1 ? 'bostad till salu' : 'bostäder till salu'}
            </Text>
            {sold_homes_count && sold_homes_count > 0 && (
              <>
                <Text color="blackAlpha.500" fontSize="sm">•</Text>
                <Text
                  color="blackAlpha.500"
                  fontSize={variant === 'compact' ? 'xs' : 'sm'}
                  noOfLines={1}
                  fontWeight="normal"
                >
                  fler än {sold_homes_count} sålda
                </Text>
              </>
            )}
          </HStack>
        </ListSkeleton>
      </HStack>

      {/* Areas count */}
      <HStack alignItems="baseline" justifyContent="flex-start">
        <ListSkeleton isLoaded={!isLoading}>
          <Text
            color="blackAlpha.600"
            fontSize={variant === 'compact' ? 'xs' : 'sm'}
            noOfLines={1}
            fontWeight="normal"
          >
            {areas_count || 0} {areas_count === 1 ? 'aktivt område' : 'aktiva områden'}
          </Text>
        </ListSkeleton>
      </HStack>
    </Stack>
  );
};
